import { useEffect, useState } from 'react';
import { AlertTriangle, Cable, Trash2, Video } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import {
  createDeviceInstallation, getDeviceInstallations, closeDeviceInstallation, updateDeviceCapture,
  type DeviceDto, type CaptureModeDto,
} from '../../lib/api/endpoints';
import { ApiError } from '../../lib/api/client';
import type { Vehicle } from '../../types/vehicle';

interface DeviceInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: DeviceDto | null;
  vehicles: Vehicle[];
  // Solo el Administrador enciende/apaga la grabación continua (ADR-027 §5.1):
  // consume el plan de datos de la SIM 24/7. El Supervisor no (issue #205).
  canManageCapture: boolean;
}

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) {
    // 409: la vigencia se solapa con otra sobre el mismo vehículo (ADR-007).
    if (err.status === 409) {
      return 'Ese vehículo ya tiene un equipo instalado con vigencia abierta. Retíralo primero.';
    }
    return err.detail ? `${err.title}: ${err.detail}` : err.title;
  }
  return err instanceof Error ? err.message : 'Error desconocido.';
}

// Issue #204/#205 — gestión del equipo: instalar/retirar en un vehículo
// (ADR-007: la vigencia es un recurso aparte, EXCLUDE por vehículo) y encender
// la grabación continua (ADR-027 §5.1, solo Administrador). Sin instalación
// vigente el adaptador no sondea; con la captura en OFF el grabador no toma el
// equipo aunque esté instalado.
export function DeviceInstallModal({ isOpen, onClose, device, vehicles, canManageCapture }: DeviceInstallModalProps) {
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [captureMode, setCaptureMode] = useState<CaptureModeDto>('OFF');
  const [captureSaving, setCaptureSaving] = useState(false);
  const [confirmCapture, setConfirmCapture] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedVehicleId('');
      setSaving(false);
      setError('');
      setConfirmRetire(false);
      setCaptureMode(device?.captureMode ?? 'OFF');
      setCaptureSaving(false);
      setConfirmCapture(false);
    }
  }, [isOpen, device]);

  if (!device) return null;

  const installed = !!device.installedVehicleId;
  const currentVehicle = vehicles.find(v => v.id === device.installedVehicleId);
  // Solo vehículos sin equipo: instalar sobre uno ocupado devuelve 409.
  const freeVehicles = vehicles.filter(v => !v.deviceId);

  const handleInstall = async () => {
    if (!selectedVehicleId) {
      setError('Selecciona el vehículo donde se instala el equipo.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await createDeviceInstallation({
        imei: device.imei,
        vehicleId: selectedVehicleId,
        validFrom: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRetire = async () => {
    setError('');
    setSaving(true);
    try {
      // El `Device` trae el vehículo vigente pero no el id de la instalación:
      // se busca la vigente (activeAt = ahora) para cerrarla.
      const page = await getDeviceInstallations({ deviceId: device.id, activeAt: new Date().toISOString() });
      const active = page.items[0];
      if (!active) {
        setError('No se encontró una instalación vigente para retirar. Recarga e intenta de nuevo.');
        return;
      }
      await closeDeviceInstallation(active.id, new Date().toISOString());
      onClose();
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCapture = async (mode: CaptureModeDto) => {
    setError('');
    setCaptureSaving(true);
    try {
      await updateDeviceCapture(device.id, mode);
      setCaptureMode(mode);
      setConfirmCapture(false);
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setCaptureSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Gestión del equipo — IMEI ${device.imei}`} size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {installed ? (
          <>
            <p style={{ fontSize: '13.5px', margin: 0, color: 'var(--text-secondary)' }}>
              Instalado en <strong style={{ color: 'var(--text-primary)' }}>{currentVehicle?.plate ?? 'un vehículo'}</strong>.
              Retirarlo cierra la vigencia: el adaptador deja de sondear el equipo y el vehículo
              no recibirá más posición ni video hasta una nueva instalación.
            </p>
            {!confirmRetire ? (
              <Button variant="danger" leftIcon={<Trash2 size={15} />} onClick={() => setConfirmRetire(true)} disabled={saving}>
                Retirar del vehículo
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="danger" onClick={() => void handleRetire()} isLoading={saving}>
                  Confirmar retiro
                </Button>
                <Button variant="ghost" onClick={() => setConfirmRetire(false)} disabled={saving}>
                  Cancelar
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={{ fontSize: '13.5px', margin: 0, color: 'var(--text-secondary)' }}>
              Asocia este equipo a un vehículo. Desde ese momento el adaptador lo sondea y el
              vehículo empieza a recibir posición y video.
            </p>
            {freeVehicles.length > 0 ? (
              <Select
                aria-label="Vehículo"
                value={selectedVehicleId}
                onChange={e => setSelectedVehicleId(e.target.value)}
                options={[
                  { value: '', label: 'Selecciona un vehículo' },
                  ...freeVehicles.map(v => ({ value: v.id, label: `${v.plate} — ${v.brand} ${v.model}`.trim() })),
                ]}
              />
            ) : (
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                No hay vehículos sin equipo disponibles. Crea un vehículo primero, o retira el equipo
                que tenga instalado el vehículo de destino.
              </p>
            )}
            {freeVehicles.length > 0 && (
              <Button variant="primary" leftIcon={<Cable size={15} />} onClick={() => void handleInstall()} isLoading={saving}>
                Instalar
              </Button>
            )}
          </>
        )}

        {/* Grabación continua (issue #205) — solo Administrador. Independiente de
            la instalación: es una bandera del equipo. Sin esto el grabador no
            toma el equipo aunque esté instalado y con cámaras declaradas. */}
        {canManageCapture && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Video size={15} />
              <span style={{ fontSize: '13.5px', fontWeight: 700 }}>Grabación continua (24/7)</span>
              <span style={{
                padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 700,
                color: captureMode === 'CONTINUOUS' ? 'var(--low)' : 'var(--text-muted)',
                background: captureMode === 'CONTINUOUS' ? 'var(--low-bg)' : 'var(--bg-elevated)',
              }}>
                {captureMode === 'CONTINUOUS' ? 'Encendida' : 'Apagada'}
              </span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
              Graba todas las cámaras sin parar y consume el plan de datos de la SIM. Debe estar
              encendida para que el histórico y la evidencia de video existan.
            </p>
            {captureMode === 'CONTINUOUS' ? (
              <Button variant="ghost" onClick={() => void handleCapture('OFF')} isLoading={captureSaving}>
                Apagar grabación
              </Button>
            ) : !confirmCapture ? (
              <Button variant="primary" leftIcon={<Video size={15} />} onClick={() => setConfirmCapture(true)} disabled={captureSaving}>
                Encender grabación
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="primary" onClick={() => void handleCapture('CONTINUOUS')} isLoading={captureSaving}>
                  Confirmar (consume datos)
                </Button>
                <Button variant="ghost" onClick={() => setConfirmCapture(false)} disabled={captureSaving}>
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            fontSize: '12.5px', color: '#EF4444',
          }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
